import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { localize, localizeSections } from "@/lib/localize";
import { useInitiatives } from "@/lib/useInitiatives";
import InitiativeCard from "@/components/InitiativeCard";
import { ArrowLeft } from "lucide-react";

export default function InitiativePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [raw, setRaw] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { byCategory } = useInitiatives();

  useEffect(() => {
    setRaw(null);
    setNotFound(false);
    window.scrollTo({ top: 0 });
    api.get(`/initiatives/${slug}`)
      .then((r) => setRaw(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const data = useMemo(() => localize(raw, i18n.language, ["title", "short", "tagline", "summary"]), [raw, i18n.language]);
  const sections = useMemo(() => localizeSections(raw, i18n.language), [raw, i18n.language]);
  const related = data ? (byCategory[data.category] || []).filter((x) => x.slug !== data.slug).slice(0, 3) : [];

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-ink/70">
        <div className="font-mono text-sm">{t("initiative.not_found")}</div>
        <Link to="/" className="mt-4 text-teal">{t("initiative.back_home")}</Link>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="pb-24">
      {/* Hero */}
      <section className="relative band-dark circuit-bg min-h-[52vh] flex items-end overflow-hidden">
        {data.hero_image && (
          <img src={data.hero_image} alt="" role="presentation" className="absolute inset-y-0 right-0 w-[60%] h-full object-cover opacity-30 mix-blend-luminosity" />
        )}
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-16 w-full">
          <Link to="/initiatives" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-8" data-testid="initiative-back">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {t("initiative.back")}
          </Link>
          <div className="tag mb-3">{t(`initiative.categories.${data.category}`)}</div>
          <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-[1] max-w-4xl" data-testid="initiative-title">
            {data.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/85 max-w-3xl">{data.tagline}</p>
        </div>
      </section>

      {/* Stats */}
      {data.stats?.length > 0 && (
        <section className="border-b border-ink/10 bg-surface">
          <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-ink/10">
            {data.stats.map((s, i) => (
              <div key={s.label || i} className="bg-white p-6">
                <div className="font-display font-black text-brand text-2xl leading-tight">{s.value}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ink/60">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Summary + sections */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4" role="complementary" aria-label="Initiative summary">
            <div className="lg:sticky lg:top-28">
              <div className="tag mb-3">{t("initiative.summary")}</div>
              <p className="text-ink/85 text-lg leading-relaxed">{data.summary}</p>
            </div>
          </aside>

          <div className="lg:col-span-8 space-y-10">
            {sections.map((sec, i) => (
              <div key={sec.heading || i} className="border-l-2 border-teal pl-6">
                <div className="font-mono text-[10px] uppercase tracking-widest text-teal mb-3">0{i + 1}</div>
                <h2 className="font-display font-bold text-brand text-2xl mb-4">{sec.heading}</h2>
                <p className="text-ink/75 leading-relaxed whitespace-pre-line">{sec.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-surface border-t border-ink/10" aria-labelledby="related-heading">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <h2 id="related-heading" className="tag mb-6">{t("initiative.related")}</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map((it, i) => <InitiativeCard key={it.slug} item={it} index={i} compact />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
